import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { GlobalVariables } from '../data/global-variables';
import { PageSimplifiedStudy, SimplifiedStudy, Study } from '../data/study';
import { AlertService } from './alert.service';
import { ConfigService } from './config.service';
import { StudyFilters } from '../data/filters';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class StudiesService {

  // Si la lógica de obtención de estudios aumenta en complejidad o resposibilidades,
  // considerar mover esta gestión (con sus métodos) a un servicio específico (ObtainingStudiesService)
  private obtainingStudies: Set<string> = new Set();

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private globalVariables: GlobalVariables,
    private alertService: AlertService
  ) { }

  getStudiesByToken(studyToken: string): Promise<Study[]> {
    return new Promise<Study[]>((resolve, reject) => {
      if (!environment.demo) {
        this.configService.BOOK_URL.then(confURL => {
          const url = `${confURL}/bookAccess/getStudiesByToken?token=${studyToken}`;
          this.http.get<Study[]>(url, { withCredentials: true, responseType: 'json' }).subscribe(res => {
            resolve(Study.sanitizeAll(res));
          })
        }, error => reject(error))
      } else {
        resolve([Study.getExample(this.configService.company)])
      }
    });
  };

  downloadStudies(studyUIDs: string[]): Observable<HttpEvent<Blob> | null> {
    if (!environment.demo) {
      const url = `${this.configService.syncBOOK_URL}/bookAccess/getStudies`;
      return this.http.post(url, studyUIDs, { withCredentials: true, responseType: 'blob', reportProgress: true, observe: 'events' });
    } else {
      const subject = new Subject<HttpEvent<Blob> | null>();
      setTimeout(() => {
        subject.next(null);
        subject.complete();
      }, 25);
      return subject.asObservable();
    }
  }

  /**
   * Obtiene los bytes del informe para un array de studyUIDs
   * @param studyUIDs - Array de UIDs de estudios
   * @returns Observable con los bytes del informe en formato ArrayBuffer
   */
  getReportBytes(studyUIDs: string[]): Observable<ArrayBuffer> {
    if (!environment.demo) {
      const url = `${this.configService.syncBOOK_URL}/bookAccess/getReportBytesByStudyUID`;
      return this.http.post(url, studyUIDs, { withCredentials: true, responseType: 'arraybuffer' });
    } else {
      const url = 'assets/demo/report.pdf';
      return this.http.get(url, { responseType: 'arraybuffer' });
    }
  }

  downloadReport(studyUIDs: string[]) {
    // console.log('studyUIDs', studyUIDs);

    if (!environment.demo) {
      const url = `${this.configService.syncBOOK_URL}/report/downloadReportsByStudyUID`;
      this.http.post(url, studyUIDs[0], { withCredentials: true, responseType: 'arraybuffer' }).subscribe(reportBytes => {
        // console.log('reportBytes', reportBytes);
        if (reportBytes == null || reportBytes.byteLength == 0) {
          this.alertService.alert('Este estudio no dispone de informe. Póngase en contacto con el centro para más información')
        } else {
          const blob = new Blob([reportBytes], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);

          this.downloadBlob(blobUrl);

        }
      }, error => this.alertService.alert('No se ha podido descargar el informe. Por favor, actualice la página e inténtelo de nuevo más tarde'))
    } else {
      const url = 'assets/demo/report.pdf';
      this.downloadBlob(url);
    }
  }


  printBlob(blobUrl: string) {
    let iframe = document.createElement('iframe'); //load content in an iframe to print later
    document.body.appendChild(iframe);

    iframe.style.display = 'none';
    iframe.src = blobUrl;
    iframe.onload = function () {
      setTimeout(function () {
        iframe.focus();
        iframe.contentWindow?.print();
      }, 1);
    };
  }

  downloadBlob(blobUrl: string) {
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = blobUrl;
    a.download = 'informe.pdf';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(blobUrl);
  }

  askForStudy(studyUID: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!environment.demo) {
        this.configService.BOOK_URL.then(BOOK_URL => {
          const url = BOOK_URL + '/bookAccess/askForStudy?studyUID=' + studyUID;
          this.http.get<void>(url, { withCredentials: true, responseType: 'json' }).subscribe(res => {
            resolve();
          }, error => {
            reject(error);
          })
        });
      } else {
        resolve();
      }
    })
  }

  checkStudyStatus(studyUID: string): Observable<boolean> {
    return new Observable<boolean>(observer => {
      if (!environment.demo) {
        this.configService.BOOK_URL.then(BOOK_URL => {
          const url = BOOK_URL + '/bookAccess/checkStudyStatus?studyUID=' + studyUID;
          this.http.get<boolean>(url, { withCredentials: true, responseType: 'json' }).subscribe(res => {
            observer.next(res);
            observer.complete();
          }, error => {
            observer.error(error);
            observer.complete();
          })
        });
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }

  // Comprueba que el estudio tiene archivos DICOM en la carpeta "location" de base de datos
  checkIfStudyHasFiles(studyUID: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.configService.BOOK_URL.then(url => {
        this.http.post<boolean>(`${url}/study/checkIfStudyHasFiles`, studyUID, { withCredentials: true, responseType: 'json' }).subscribe({
          next: res => {
            resolve(res)
          },
          error: error => reject(error)
        })
      })
    })
  }

  //============================== PETICIONES DE BOOKS AL GENERATOR ===============================

  getPageStudiesByUserId(userId: number, filters: StudyFilters): Promise<PageSimplifiedStudy> {
    return new Promise((resolve, reject) => {
      this.configService.BOOK_URL.then(url => {
        this.http.post<PageSimplifiedStudy>(`${url}/study/getStudiesListByUserId`, {userId, filters}, { withCredentials: true, responseType: 'json' }).subscribe({
          next: res => {
            resolve(PageSimplifiedStudy.sanitize(res))
          },
          error: error => reject(error)
        })
      })
    })
  }

  getOtherPatientStudies(studyUID: string): Promise<SimplifiedStudy[]> {
    return new Promise((resolve, reject) => {
      this.configService.BOOK_URL.then(url => {
        this.http.post<SimplifiedStudy[]>(`${url}/study/getOtherStudiesOfSamePatient`, studyUID, { withCredentials: true, responseType: 'json' }).subscribe({
          next: res => {
            resolve(SimplifiedStudy.sanitizeAll(res))
          },
          error: error => reject(error)
        })
      })
    })
  }

  /**
   * Solicita la obtención de un estudio al servidor
   * Gestiona automáticamente el estado de "obteniendo" y los reintentos
   * @param studyUID UID del estudio a obtener
   * @returns Promise que se resuelve cuando se completa la solicitud
   */
  obtainStudy(studyUID: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!studyUID) {
        reject('No study UID provided');
        return;
      }

      if (this.obtainingStudies.has(studyUID)) {
        reject('Study is already being obtained');
        return;
      }

      this.obtainingStudies.add(studyUID);

      setTimeout(() => {
        this.askForStudy(studyUID)
          .then(() => {
            resolve();
          })
          .catch(error => {
            this.obtainingStudies.delete(studyUID);
            this.alertService.alert('Ha ocurrido un error tratando de recuperar el estudio. Contacte con su centro');
            reject(error);
          });
      }, 1200);
    });
  }

  /**
   * Verifica si un estudio está en proceso de obtención
   * @param studyUID UID del estudio
   * @returns true si el estudio se está obteniendo, false en caso contrario
   */
  isObtaining(studyUID: string): boolean {
    return this.obtainingStudies.has(studyUID);
  }

  /**
   * Limpia el estado de obtención de un estudio específico
   * Útil para resetear el estado manualmente si es necesario
   * @param studyUID UID del estudio
   */
  clearObtainingStatus(studyUID: string): void {
    this.obtainingStudies.delete(studyUID);
  }

  /**
   * Limpia todos los estados de obtención
   * Útil al cerrar componentes o resetear estados
   */
  clearAllObtainingStatus(): void {
    this.obtainingStudies.clear();
  }

  
}
